/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import CampaignModel from 'cio/models/campaign';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import { FrequencyOption, LeadDaysType } from 'cio/utils/campaigns/date-triggered';
import {
  EMAIL_AND_ID_IDENTIFIER_CONFIG,
  isAttributeEnabledIdentifier,
  isObjectAttributeIdentifier,
} from 'cio/utils/identifiers';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';
import ObjectTypesService from 'cio/services/object-types';
import ObjectTypeModel from 'cio/models/object-type';
import pluralize from 'cio/utils/pluralize';
import { isEmpty } from '@ember/utils';

export interface CampaignsTriggerSetupDateArgs {
  isCampaignOverview: boolean;
  hasUnsavedAttributes: boolean;
  campaign: CampaignModel;
  dateTriggeredAttribute: string;
  dateTriggeredLeadDays: number;
  dateTriggeredStartTime: string;
  dateTriggeredZone: string;
  dateTriggeredFallbackZone: string;
  dateTriggeredFrequency: FrequencyOption;
  maxLeadDays: number;
  triggerIsSaved: boolean;
  summary: string;
  objectTypeId?: number;
  toggleSidebar: () => unknown;
  updateTopLevelValues: <
    K extends
      | 'objectTypeId'
      | 'dateTriggeredAttribute'
      | 'dateTriggeredLeadDays'
      | 'dateTriggeredFrequency'
      | 'dateTriggeredStartTime'
      | 'dateTriggeredZone'
      | 'dateTriggeredFallbackZone',
    V extends CampaignsTriggerSetupDateArgs[K],
  >(
    values: Record<K, V | null>,
  ) => unknown;
}

enum Views {
  typeSelect = 'typeSelect',
  dateConfig = 'dateConfig',
}

export default class CampaignsTriggerSetupDate extends Component<CampaignsTriggerSetupDateArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service objectTypes!: ObjectTypesService;

  readonly frequencyOptions = [
    { value: FrequencyOption.once, label: 'Once (triggers one time on the date)' },
    { value: FrequencyOption.monthly, label: 'Monthly (triggers every month starting on the date)' },
    { value: FrequencyOption.yearly, label: 'Yearly (triggers every year starting on the date)' },
  ];
  readonly leadDaysTypeOptions = [
    { value: LeadDaysType.on, label: 'On the date' },
    { value: LeadDaysType.before, label: 'Before the date' },
    { value: LeadDaysType.after, label: 'After the date' },
  ];
  readonly frequencyOption = FrequencyOption;
  readonly leadDaysType = LeadDaysType;
  readonly views = Views;
  @tracked days = 1;
  @tracked selectedLeadDaysType: LeadDaysType = LeadDaysType.on;
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked objectTypeId: number | undefined | null = this.args.objectTypeId;
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked dateTriggeredAttribute: CampaignsTriggerSetupDateArgs['dateTriggeredAttribute'] =
    this.args.dateTriggeredAttribute;
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked dateTriggeredFrequency: CampaignsTriggerSetupDateArgs['dateTriggeredFrequency'] =
    this.args.dateTriggeredFrequency;
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked dateTriggeredStartTime: CampaignsTriggerSetupDateArgs['dateTriggeredStartTime'] =
    this.args.dateTriggeredStartTime;
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked dateTriggeredZone: CampaignsTriggerSetupDateArgs['dateTriggeredZone'] = this.args.dateTriggeredZone;
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked dateTriggeredFallbackZone: CampaignsTriggerSetupDateArgs['dateTriggeredFallbackZone'] =
    this.args.dateTriggeredFallbackZone;
  @tracked view = Views.dateConfig;

  get objectType() {
    if (this.objectTypeId == null) {
      return;
    }
    return this.objectTypes.enabledObjectTypes.find(({ id }) => Number(id) === Number(this.objectTypeId));
  }

  get availableAttributes() {
    if (this.objectType) {
      return this.objectType.sentAttributes.filter(
        (attr: string) => !isObjectAttributeIdentifier(attr) && attr !== CREATED_IN_CIO_AT,
      );
    }
    return this.environment.sentAttributes.filter(
      (attr) => !isAttributeEnabledIdentifier(attr, EMAIL_AND_ID_IDENTIFIER_CONFIG) && attr !== CREATED_IN_CIO_AT,
    );
  }

  get hasMoreAttributes() {
    return this.objectType?.moreAttributes ?? this.environment.moreAttributes;
  }

  get maxleadDaysExceededTooltipContent() {
    return `A ${this.dateTriggeredFrequency} campaign cannot be triggered more than ${this.args.maxLeadDays} days before or after the trigger date`;
  }

  constructor(owner: unknown, args: CampaignsTriggerSetupDateArgs) {
    super(owner, args);

    if (this.args.dateTriggeredLeadDays) {
      this.setInternalLeadDays();
    }

    if (this.args.triggerIsSaved || !isEmpty(this.args.objectTypeId) || !isEmpty(this.args.dateTriggeredAttribute)) {
      this.view = Views.dateConfig;
    }
  }

  get summary() {
    let values = [];
    let {
      dateTriggeredAttribute,
      dateTriggeredFrequency: frequency,
      dateTriggeredZone: zone,
      dateTriggeredStartTime: startTime,
      dateTriggeredFallbackZone: fallbackZone,
    } = this;
    let leadDays = this.getCampaignLeadDays;
    if (frequency === FrequencyOption.yearly) {
      values.push('every year');
    } else if (frequency === FrequencyOption.monthly) {
      values.push('every month');
    } else {
      values.push('once');
    }
    if (leadDays > 0) {
      values.push(`${leadDays} ${pluralize(leadDays, 'day')} after`);
    } else if (leadDays < 0) {
      values.push(`${-leadDays} ${pluralize(-leadDays, 'day')} before`);
    } else {
      values.push('on');
    }
    return `${values.join(' ')} the ${dateTriggeredAttribute || '(select attribute)'} date at ${startTime} ${
      zone || fallbackZone
    }.`;
  }

  get hasChanges() {
    return (
      this.args.objectTypeId !== this.objectTypeId ||
      this.args.dateTriggeredAttribute !== this.dateTriggeredAttribute ||
      this.args.dateTriggeredLeadDays !== this.getCampaignLeadDays ||
      this.args.dateTriggeredStartTime !== this.dateTriggeredStartTime ||
      this.args.dateTriggeredZone !== this.dateTriggeredZone ||
      this.args.dateTriggeredFallbackZone !== this.dateTriggeredFallbackZone ||
      this.args.dateTriggeredFrequency !== this.dateTriggeredFrequency
    );
  }

  @action
  onChangeDays(value: string) {
    let days = Number(value);
    if (days < 1) {
      days = 1;
    }
    this.days = days;
  }

  @action
  onSelectLeadDaysType(type: LeadDaysType) {
    this.selectedLeadDaysType = type;
  }

  @action
  updateFrequency(value: FrequencyOption) {
    this.dateTriggeredFrequency = value;
  }

  get getCampaignLeadDays() {
    switch (this.selectedLeadDaysType) {
      case LeadDaysType.after: {
        return this.days;
      }
      case LeadDaysType.before: {
        return 0 - this.days;
      }
      case LeadDaysType.on:
      default: {
        return 0;
      }
    }
  }

  setInternalLeadDays() {
    this.days = Math.abs(Number(this.args.dateTriggeredLeadDays));
    this.selectedLeadDaysType = this.args.dateTriggeredLeadDays > 0 ? LeadDaysType.after : LeadDaysType.before;
  }

  @action
  onSelectType(option: ObjectTypeModel) {
    this.objectTypeId = Number(option?.id) || 0;
    this.view = this.views.dateConfig;
  }

  @action
  onBack() {
    this.objectTypeId = null;
    this.view = this.views.typeSelect;
  }

  @action
  saveAndClose() {
    let objectTypeIdValue = this.objectTypeId;
    if (objectTypeIdValue === 0) {
      objectTypeIdValue = null;
    }
    this.args.updateTopLevelValues({
      objectTypeId: objectTypeIdValue,
      dateTriggeredAttribute: this.dateTriggeredAttribute,
      dateTriggeredLeadDays: this.getCampaignLeadDays,
      dateTriggeredFrequency: this.dateTriggeredFrequency,
      dateTriggeredZone: this.dateTriggeredZone,
      dateTriggeredStartTime: this.dateTriggeredStartTime,
      dateTriggeredFallbackZone: this.dateTriggeredFallbackZone,
    });
    this.args.toggleSidebar();
  }
}
